import { useState } from 'react';
import { format, isValid } from 'date-fns';

import { Alert, Box, CloseButton, Text, TextButton } from '@zillow/constellation-10';

import { RpErrorBoundary } from '@zg-rentals/rp-bootstrap-browser';
import { EXTERNAL_URLS } from '@zg-rentals/zillow-rental-manager/constants';

import { getDaysLeft, useIdentityVerification } from '../../hooks/useIdentityVerification';

import { EXTERNAL_ROUTES } from '../../constants/urls';

import type { IdentityVerification } from '../../hooks/useIdentityVerification';
import type {
  IdentityVerificationRestrictedAction,
  IdentityVerificationStatus,
} from '../../types/identityVerification';

// Placeholder until the BE exposes its renewal window setting.
const RENEWAL_WINDOW_DAYS = 30;

const ACTION_PHRASES: Record<IdentityVerificationRestrictedAction, string> = {
  PUBLISH_LISTING: 'publish listings',
  INVITE_TO_APPLY: 'invite renters to apply',
  SETUP_PAYMENTS: 'set up payments',
};

const KEEP_PUBLISHING = 'keep publishing listings';

const toActionPhrase = (actions: Array<IdentityVerificationRestrictedAction>) => {
  const phrases = actions.map((action) => ACTION_PHRASES[action]);
  if (phrases.length <= 1) return phrases[0];
  return `${phrases.slice(0, -1).join(', ')} and ${phrases.at(-1)}`;
};

const formatDate = (timestamp: number) => (isValid(timestamp) ? format(timestamp, 'MMMM d, yyyy') : undefined);

type BannerContent = {
  tone: 'info' | 'warning' | 'critical';
  header: string;
  body: string;
  cta?: { label: string; href: string };
  isDismissible?: boolean;
};

const verifyCta = (label: string) => ({ label, href: EXTERNAL_ROUTES.VERIFY_IDENTITY });

export const getBannerContent = (
  { entry, restrictedActions, isEnforced, daysLeft }: IdentityVerification,
  now: number,
): BannerContent | undefined => {
  if (!entry) return undefined;

  const actions = toActionPhrase(restrictedActions);

  switch (entry.status) {
    case 'NOT_STARTED':
      if (isEnforced) {
        return {
          tone: 'warning',
          header: 'Verify your identity',
          body: `Required to ${actions}`,
          cta: verifyCta('Verify now'),
        };
      }
      if (entry.enrollment && daysLeft !== undefined && daysLeft > 0) {
        const deadline = formatDate(entry.enrollment.gracePeriodEndsAt);
        if (!deadline) return undefined;
        return {
          tone: 'info',
          header: 'Verify your identity',
          body: `You can keep using all features for now. Verify by ${deadline} to avoid interruptions when publishing a new listing or reactivating an off-market listing.`,
          cta: verifyCta('Verify now'),
        };
      }
      return undefined;
    case 'IN_PROGRESS':
      return {
        tone: 'info',
        header: 'Finish verifying your identity',
        body: 'You started verifying your identity. Pick up where you left off.',
        cta: verifyCta('Continue verifying'),
      };
    case 'NEEDS_REVIEW':
      return {
        tone: 'info',
        header: 'Your identity verification is under review',
        body: `We’ll email you when it’s done${actions ? `. You can’t ${actions} until then.` : ''}`,
        isDismissible: true,
      };
    case 'FAILED':
      return {
        tone: 'critical',
        header: 'We couldn’t verify your identity',
        body: 'Contact support if you think this is a mistake',
        cta: { label: 'Contact support', href: EXTERNAL_URLS.SUPPORT_EMAIL },
      };
    case 'REVOKED':
      return {
        tone: 'critical',
        header: 'Verify your identity again',
        body: `Your identity verification is no longer valid. Verify again to ${actions ?? KEEP_PUBLISHING}.`,
        cta: verifyCta('Verify again'),
      };
    case 'EXPIRED':
      return {
        tone: 'warning',
        header: 'Your identity verification expired',
        body: `Verify again to ${actions ?? KEEP_PUBLISHING}`,
        cta: verifyCta('Verify again'),
      };
    case 'VERIFIED': {
      const { expiresAt } = entry;
      if (expiresAt === undefined || getDaysLeft(expiresAt, now) > RENEWAL_WINDOW_DAYS) return undefined;
      const expiresOn = formatDate(expiresAt);
      if (!expiresOn) return undefined;
      return {
        tone: 'info',
        header: 'Renew your identity verification',
        body: `Your verification expires on ${expiresOn}. Renew it to ${KEEP_PUBLISHING}.`,
        cta: verifyCta('Renew'),
        isDismissible: true,
      };
    }
  }
};

/** Account-wide IDV status. Not dismissible while its CTA resolves it. */
const IdentityVerificationAlert = () => {
  const verification = useIdentityVerification();
  const [dismissedStatus, setDismissedStatus] = useState<IdentityVerificationStatus>();

  const content = getBannerContent(verification, Date.now());
  const status = verification.entry?.status;

  if (!content || (content.isDismissible && dismissedStatus === status)) return null;

  return (
    // Rendered above Page.Content, so it repeats the page's width and gutters like bannerBoxRecipe.
    <Box
      css={{
        boxSizing: 'content-box',
        maxWidth: 'breakpoint-xxl',
        marginBlock: 'default',
        marginInline: 'auto',
        paddingInline: { base: 'device.margin.mobile', md: 'device.margin.tablet', xl: 'device.margin.desktop' },
      }}
    >
      <Alert
        data-testid="IdentityVerificationBanner"
        tone={content.tone}
        isOpen
        onClose={() => setDismissedStatus(status)}
        closeButton={content.isDismissible ? <CloseButton /> : null}
        actionButton={
          content.cta && (
            <TextButton asChild>
              <a href={content.cta.href}>{content.cta.label}</a>
            </TextButton>
          )
        }
      >
        <Text textStyle="body-bold" css={{ display: 'block' }}>
          {content.header}
        </Text>
        {content.body}
      </Alert>
    </Box>
  );
};

// Its own boundary, so bad verification data hides the banner instead of breaking the page.
const IdentityVerificationBanner = () => (
  <RpErrorBoundary fallback={<></>}>
    <IdentityVerificationAlert />
  </RpErrorBoundary>
);

export default IdentityVerificationBanner;
